import { useState, useEffect } from 'react'
import { capstoneHintService } from '../../services/capstoneHintService'
import toast from 'react-hot-toast'
import {
  LightBulbIcon,
  LockClosedIcon,
  ClockIcon,
  CheckCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import { LightBulbIcon as LightBulbIconSolid } from '@heroicons/react/24/solid'

export default function CapstoneHintSystem({ capstoneId, onHintUnlock }) {
  const [hints, setHints] = useState([])
  const [loading, setLoading] = useState(true)
  const [unlocking, setUnlocking] = useState(null)

  useEffect(() => {
    loadHints()
  }, [capstoneId])

  const loadHints = async () => {
    try {
      setLoading(true)
      const data = await capstoneHintService.getHints(capstoneId)
      setHints(data.hints || [])
    } catch (error) {
      console.error('Failed to load hints:', error)
      toast.error('Failed to load hints')
    } finally {
      setLoading(false)
    }
  }

  const handleUnlockHint = async (hintId) => {
    try {
      setUnlocking(hintId)
      const data = await capstoneHintService.unlockHint(hintId)
      
      // Update the hint in the list
      setHints(prevHints =>
        prevHints.map(hint =>
          hint.id === hintId
            ? { ...hint, is_revealed: true, hint_text: data.hint_text }
            : hint
        )
      )

      toast.success('💡 Hint unlocked!')
      
      if (onHintUnlock) {
        onHintUnlock(hintId)
      }
    } catch (error) {
      console.error('Failed to unlock hint:', error)
      const message = error.response?.data?.message || 'Failed to unlock hint'
      toast.error(message)
    } finally {
      setUnlocking(null)
    }
  }

  const getUnlockConditionText = (hint) => {
    if (hint.unlock_condition === 'immediate') {
      return 'Available now'
    } else if (hint.unlock_condition === 'time') {
      return `Available after ${hint.unlock_minutes} minutes`
    } else if (hint.unlock_condition === 'task_completion') {
      return `Complete ${hint.required_tasks_completed} tasks to unlock`
    }
    return 'Unknown condition'
  }

  const getUnlockIcon = (hint) => {
    if (hint.is_revealed) {
      return <LightBulbIconSolid className="w-5 h-5 text-yellow-500" />
    } else if (hint.is_unlocked) {
      return <SparklesIcon className="w-5 h-5 text-blue-500" />
    } else {
      return <LockClosedIcon className="w-5 h-5 text-gray-400" />
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-20 bg-gray-200 rounded-lg"></div>
        <div className="h-20 bg-gray-200 rounded-lg"></div>
      </div>
    )
  }

  if (!hints || hints.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <LightBulbIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>No hints available for this capstone</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <LightBulbIcon className="w-5 h-5 mr-2 text-yellow-500" />
          Progressive Hints
        </h3>
        <span className="text-sm text-gray-500">
          {hints.filter(h => h.is_revealed).length} / {hints.length} revealed
        </span>
      </div>

      <div className="space-y-3">
        {hints.map((hint, index) => (
          <div
            key={hint.id}
            className={`border rounded-lg p-4 transition-all ${
              hint.is_revealed
                ? 'bg-yellow-50 border-yellow-200'
                : hint.is_unlocked
                ? 'bg-blue-50 border-blue-200'
                : 'bg-gray-50 border-gray-200'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  {getUnlockIcon(hint)}
                  <span className="ml-2 font-medium text-gray-900">
                    Hint #{index + 1}
                  </span>
                  {hint.point_cost > 0 && !hint.is_revealed && (
                    <span className="ml-2 text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                      {hint.point_cost} points
                    </span>
                  )}
                </div>

                {hint.is_revealed ? (
                  <div className="text-gray-700 leading-relaxed">
                    {hint.hint_text}
                  </div>
                ) : hint.is_unlocked ? (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      This hint is now available to unlock
                    </p>
                    <button
                      onClick={() => handleUnlockHint(hint.id)}
                      disabled={unlocking === hint.id}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                    >
                      {unlocking === hint.id ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Unlocking...
                        </>
                      ) : (
                        <>
                          <SparklesIcon className="w-4 h-4 mr-2" />
                          Reveal Hint
                          {hint.point_cost > 0 && ` (${hint.point_cost} points)`}
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-500">
                      {hint.unlock_condition === 'time' ? (
                        <ClockIcon className="w-4 h-4 mr-1" />
                      ) : hint.unlock_condition === 'task_completion' ? (
                        <CheckCircleIcon className="w-4 h-4 mr-1" />
                      ) : (
                        <LockClosedIcon className="w-4 h-4 mr-1" />
                      )}
                      {getUnlockConditionText(hint)}
                    </div>
                    <p className="text-xs text-gray-400">
                      Complete the unlock requirements to reveal this hint
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start">
          <LightBulbIcon className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">How hints work:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Hints unlock progressively as you work through the capstone</li>
              <li>Time-based hints unlock after spending enough time on the project</li>
              <li>Task-based hints unlock after completing specific tasks</li>
              <li>Some hints may cost points - use them wisely!</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
